import { LucideIcon } from 'lucide-react';
import { Link } from 'react-router';
import { useEffect } from 'react';
import { Button } from '@repo/ui/components/button';

interface StatusPageProps {
  title: string;
  description: string;
  actions?: { label: string; url: string }[];
  icon: LucideIcon;
  color?: 'red' | 'yellow' | 'green';
  children?: React.ReactNode;
  effect?: 'confetti';
}

const colorClasses = {
  red: 'text-red',
  yellow: 'text-yellow',
  green: 'text-green',
};

export function StatusPage({
  title,
  description,
  actions = [],
  icon,
  color = 'green',
  children,
  effect,
}: StatusPageProps) {
  const Icon = icon;
  const colorClass = colorClasses[color];

  useEffect(() => {
    if (effect === 'confetti') {
      import('canvas-confetti').then(({ default: confetti }) =>
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        })
      );
    }
  }, [effect]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-4 text-center">
      <Icon className={`size-16 ${colorClass}`} />
      <div className="flex max-w-md flex-col items-center gap-2">
        <h1 className="text-3xl font-semibold">{title}</h1>
        <p className="text-lg text-muted-foreground">{description}</p>
      </div>
      {actions.length > 0 && (
        <div className="flex flex-row gap-2">
          {actions.map((action) => (
            <Button key={action.label} size="xl" asChild>
              <Link to={action.url}>{action.label}</Link>
            </Button>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}
